// Tabs — accessible segmented tab strip. Controlled: parent owns the active id.
// Renders only the tab strip; callers render the active panel themselves (so
// panels can be lazy/data-driven). Arrow-key navigation + ARIA roles.
// Powers Portfolio's 4 holdings views and the fund-detail modal.
import { useRef } from 'react';
import PropTypes from 'prop-types';
import { cx } from '../../../utils/cx';
import './tabs.css';

export function Tabs({ tabs, value, onChange, fitted = false, className }) {
  const refs = useRef([]);

  const onKeyDown = (e, idx) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const dir = e.key === 'ArrowRight' ? 1 : -1;
    const next = (idx + dir + tabs.length) % tabs.length;
    refs.current[next]?.focus();
    onChange(tabs[next].id);
  };

  return (
    <div className={cx('tabs', fitted && 'tabs--fitted', className)} role="tablist">
      {tabs.map((t, i) => (
        <button
          key={t.id}
          ref={(el) => (refs.current[i] = el)}
          type="button"
          role="tab"
          id={`tab-${t.id}`}
          aria-selected={value === t.id}
          aria-controls={`panel-${t.id}`}
          tabIndex={value === t.id ? 0 : -1}
          className={cx('tabs__tab', t.short && 'tabs__tab--has-short', value === t.id && 'is-active')}
          onClick={() => onChange(t.id)}
          onKeyDown={(e) => onKeyDown(e, i)}
        >
          <span className="tabs__label-full">{t.label}</span>
          {t.short && <span className="tabs__label-short">{t.short}</span>}
        </button>
      ))}
    </div>
  );
}

Tabs.propTypes = {
  /**
   * [{ id, label, short? }] — `short` replaces `label` on narrow screens. A tab that
   * supplies no `short` keeps its full label at every width: the swap is opt-in, or a
   * caller with long labels and no abbreviation would render a strip of blank buttons.
   */
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.node.isRequired,
      short: PropTypes.node,
    }),
  ).isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  /** Stretch tabs to fill the strip width. */
  fitted: PropTypes.bool,
  className: PropTypes.string,
};

export default Tabs;
